/* ── ReportIssue — inline problem report form (Phase 6) ──────────
   Expands inline below the message action bar.
   Categories & severity aligned with D1 issues table.
   ─────────────────────────────────────────────────────────────── */

import { useState } from 'react';
import type { IssueSubmission } from '../types';
import { submitIssue } from '../api';
import { useToast } from '../contexts/ToastContext';

interface Props {
  queryId?: string;
  queryText?: string;
  responseText?: string;
  onClose: () => void;
  /** Called when report is successfully submitted (so parent can disable re-reporting) */
  onSubmitted?: () => void;
}

const DESC_MAX = 500;

const CATEGORIES: { value: IssueSubmission['category']; label: string }[] = [
  { value: 'wrong_citation', label: 'Incorrect citation' },
  { value: 'wrong_answer',   label: 'Wrong clause quoted' },
  { value: 'outdated',       label: 'Outdated information' },
  { value: 'missing_info',   label: 'Missing information' },
  { value: 'app_bug',        label: 'Confusing answer' },
  { value: 'other',          label: 'Other' },
];

const SEVERITIES: { value: IssueSubmission['severity']; label: string; emoji: string }[] = [
  { value: 'minor',     label: 'Minor',    emoji: '○' },
  { value: 'important', label: 'Major',    emoji: '◑' },
  { value: 'critical',  label: 'Critical', emoji: '●' },
];

export default function ReportIssue({ queryId, queryText, responseText, onClose, onSubmitted }: Props) {
  const [category, setCategory] = useState<IssueSubmission['category']>('wrong_citation');
  const [severity, setSeverity] = useState<IssueSubmission['severity']>('minor');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { addToast } = useToast();

  const trimmed = description.trim();
  const canSubmit = !submitting && !submitted;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);

    try {
      await submitIssue({
        category,
        severity,
        description: trimmed.slice(0, DESC_MAX),
        query_id: queryId,
        query_text: queryText,
        response_text: responseText ? responseText.slice(0, 1000) : undefined,
      });
      setSubmitted(true);
      onSubmitted?.();
      addToast('success', 'Issue reported — thanks', 3000);
      // Auto-close after 4 seconds
      setTimeout(() => onClose(), 4000);
    } catch {
      setError('Failed to submit — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="report-form" role="region" aria-label="Report a problem">
      <div className="report-form-header">
        <span className="report-form-title">
          <svg className="report-form-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
            <line x1="4" y1="22" x2="4" y2="15" />
          </svg>
          Report a Problem
        </span>
        <button
          className="report-form-close"
          onClick={onClose}
          aria-label="Close report form"
          type="button"
        >
          ✕
        </button>
      </div>

      {submitted ? (
        <div className="report-confirmation" role="status">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Report submitted — we'll review within 24 hours
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          {/* Category */}
          <div className="report-field">
            <label className="report-label" htmlFor="report-category">
              Category
            </label>
            <select
              id="report-category"
              className="report-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as IssueSubmission['category'])}
              disabled={submitting}
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          {/* Severity */}
          <div className="report-field">
            <span className="report-label">Severity</span>
            <div className="severity-picker" role="radiogroup" aria-label="Severity">
              {SEVERITIES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  role="radio"
                  className={`severity-option${severity === s.value ? ' severity-option--selected' : ''}${s.value === 'critical' ? ' severity-option--critical' : ''}`}
                  onClick={() => setSeverity(s.value)}
                  disabled={submitting}
                  aria-checked={severity === s.value}
                >
                  <span className="severity-dot" aria-hidden="true">{s.emoji}</span>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div className="report-field">
            <label className="report-label" htmlFor="report-description">
              Description <span className="report-label-optional">(optional)</span>
            </label>
            <textarea
              id="report-description"
              className="report-description"
              value={description}
              onChange={(e) => {
                if (e.target.value.length <= DESC_MAX) setDescription(e.target.value);
              }}
              placeholder="What went wrong? Any extra context helps…"
              rows={3}
              maxLength={DESC_MAX}
              disabled={submitting}
            />
            <span className="report-char-count" aria-live="polite">
              {trimmed.length}/{DESC_MAX}
            </span>
          </div>

          {error && (
            <div className="report-error" role="alert">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="report-submit"
            disabled={!canSubmit}
            aria-disabled={!canSubmit}
          >
            {submitting ? 'Submitting…' : 'Submit Report'}
          </button>
        </form>
      )}
    </div>
  );
}
